import React, { useCallback, useMemo } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import PropTypes from 'prop-types'
import { noop } from 'lodash'
import { Grid, makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import Select from '../../../molecules/Select'
import SplitButton from '../../../atoms/SplitButton'
import Skeleton from '../../../atoms/Skeleton'
import Title from './Title'
import { findFieldIndexById, mapGroupsToKeyValue } from './helpers'

const useStyles = makeStyles(() => ({
  tagsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    '& > div': {
      margin: '0 0.5rem 0.5rem 0'
    },
    '& > span': {
      margin: '0 0.5rem 0.5rem 0'
    }
  },
  tagSelectorContainer: {
    display: 'flex',
    flexDirection: 'column',
    margin: '1rem'
  },
  selectorsContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  tagsContainerColumn: {
    '& > div': {
      marginBottom: '0.75rem'
    },
    margin: '0.5rem 0',
    display: 'flex',
    flexDirection: 'column'
  },
  tag: {
    fontWeight: 'bold',
    maxWidth: 'unset !important'
  },
  skeleton: {
    borderRadius: '1rem'
  }
}))

const LevelTags = ({ name, isLoading, editMode, tagOptions }) => {
  const classes = useStyles()
  const { control } = useFormContext()
  const { fields, update, remove, insert } = useFieldArray({
    control,
    name
  })

  const onChangeGroupType = useCallback(
    (groupTypeId, index) => (groupId) => {
      const { id: fieldId } =
        fields.find(
          ({ groupTypeId: _groupTypeId }) => _groupTypeId === groupTypeId
        ) || {}

      const tag = { groupId, groupTypeId }

      if (fieldId) {
        const fieldIndex = findFieldIndexById(fieldId, fields)
        update(fieldIndex, tag)
      } else {
        insert(index, tag)
      }
    },
    [fields, update, insert]
  )

  const onDeleteTag = useCallback(
    (fieldId) => {
      const fieldIndex = findFieldIndexById(fieldId, fields)
      remove(fieldIndex)
    },
    [fields, remove]
  )

  const keyValueGroups = useMemo(
    () => mapGroupsToKeyValue(tagOptions),
    [tagOptions]
  )

  const fieldColors = useMemo(() => {
    return fields.reduce(
      (acc, { id, groupTypeId }) => ({
        ...acc,
        [id]: tagOptions?.find(({ value }) => groupTypeId === value)?.color
      }),
      {}
    )
  }, [fields, tagOptions])

  const renderTags = useMemo(() => {
    if (isLoading) {
      return (
        <>
          <Skeleton
            width='10rem'
            height='1.5rem'
            className={classes.skeleton}
          />
          <Skeleton
            width='10rem'
            height='1.5rem'
            className={classes.skeleton}
          />
          <Skeleton
            width='10rem'
            height='1.5rem'
            className={classes.skeleton}
          />
        </>
      )
    }
    return fields.map((field) => {
      return (
        <div key={field.id}>
          <SplitButton
            readOnly={!editMode}
            buttonClassName={classes.tag}
            iconFontSize='1rem'
            backgroundColor={fieldColors[field.id]}
            text={keyValueGroups?.[field.groupTypeId]?.[field.groupId]}
            onClickIcon={editMode ? () => onDeleteTag(field.id) : noop}
          />
        </div>
      )
    })
  }, [
    fields,
    editMode,
    isLoading,
    fieldColors,
    onDeleteTag,
    keyValueGroups,
    classes.tag,
    classes.skeleton
  ])

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Title>Tags</Title>
      </Grid>
      <Grid item xs={12} md={editMode ? 3 : 6}>
        <div
          className={clsx({
            [classes.tagsContainer]: !editMode,
            [classes.tagsContainerColumn]: editMode
          })}
        >
          {renderTags}
        </div>
      </Grid>
      {editMode ? (
        <Grid item xs={12} md={3}>
          <div className={classes.selectorsContainer}>
            {tagOptions?.map(({ label, value, options }, index) => {
              const selectedOption = fields.find(
                ({ groupTypeId }) => groupTypeId === value
              )
              return (
                <div key={value} className={classes.tagSelectorContainer}>
                  <Title>{label}</Title>
                  <Select
                    value={selectedOption?.groupId}
                    options={options}
                    showCheckMarOnSelectedItems
                    onChange={onChangeGroupType(value, index)}
                  />
                </div>
              )
            })}
          </div>
        </Grid>
      ) : null}
    </Grid>
  )
}

LevelTags.propTypes = {
  name: PropTypes.string,
  editMode: PropTypes.bool,
  tagOptions: PropTypes.array,
  isLoading: PropTypes.bool
}

export default LevelTags
